import { z } from 'zod';
import { optionalText } from '../fields/text';
import { defineForm } from '../types';
import { ENTRA_USER_ATTRIBUTES, pickEntraUser } from '../catalogues/entra-user';

/**
 * CEL Electrical Contractors (customer 1048) — new hire onboarding.
 *
 * One form covering both paper appendices, REV#002 NOV 2025:
 *
 * - **22(a)** New Hire Onboarding Request – General Field Employee
 * - **22(b)** New Hire Onboarding Request – Employee w/IT
 *
 * 22(b) is 22(a) plus eleven fields — four entitlement boxes in Other Information and
 * the IT Requirements band — so rather than two near-identical forms this one opens
 * with a mandatory `hireType` and reveals the extra eleven only for w/IT. That removes
 * the "which appendix am I?" question the person filling it used to have to answer
 * before they could start. (The paper form's "CEL Email" tick-box is not among them: a
 * hire with IT always gets a CEL account, so the box would always be ticked.)
 *
 * Two consequences worth knowing:
 *
 * - The eleven conditional fields are `.optional()` in the object and made mandatory by
 *   the `superRefine` below when `hireType` is `employee-with-it`. A discriminated union
 *   would express this more directly but is not a `ZodObject`, which the form registry
 *   and its invariants require.
 * - A field-employee submission omits those eleven keys entirely rather than sending
 *   them as `false`. The renderer drops unmounted fields, so the payload carries only
 *   what was actually asked.
 *
 * One place where the paper form and this one deliberately differ: the paper form's
 * tick-boxes are checkboxes here, and an unticked box cannot be told apart from a box
 * nobody read — the same trade the paper form makes.
 */

/**
 * The one picker decides everything else. A **general field employee** (22(a)) is added
 * to CEL's address book as a contact and gets no account, so their email is optional.
 * An **employee with IT** (22(b)) gets a CEL account — so their email is required, it
 * is the sign-in name — and the IT band is revealed.
 */
export const HIRE_TYPES = ['field-employee', 'employee-with-it'] as const;

/** Whether a hire type gets an Entra account: only a hire with IT. */
export function getsEntraAccount(hireType: (typeof HIRE_TYPES)[number]): boolean {
  return hireType === 'employee-with-it';
}

/** The section bands the paper appendices are divided into, in the order they appear. */
export const SECTIONS = {
  general: 'General Information',
  safety: 'Safety Setup',
  /** The paper form's Other Information and Other Notes bands, shown as one. */
  otherInformation: 'Other Information',
  itRequirements: 'IT Requirements',
} as const;

/**
 * The hire's name and phone are Entra attributes as well as paper-form answers, so they
 * come from the shared catalogue under Graph's names and are asked once. (`jobTitle` is
 * a Graph name too, but CEL requires it where the catalogue does not, so it keeps its
 * own schema below.)
 *
 * The paper form's Email is the hire's sign-in address, so it is stored as Graph's
 * `userPrincipalName` under CEL's own label; and every CEL branch is in Canada, so
 * `usageLocation` is filled by the schema and never shown. Between them a with-IT
 * hire's payload is a complete Entra create body without a band the paper form lacks.
 */
const names = pickEntraUser(['givenName', 'surname'], { section: SECTIONS.general });
const phone = pickEntraUser(['mobilePhone'], { section: SECTIONS.general });

/** CEL's branch list, in the order their own dropdown shows it. */
export const BRANCHES = [
  '100-saskatchewan',
  '200-manitoba',
  '300-calgary',
  '320-camrose',
  '360-red-deer',
  '600-corporate',
] as const;

/** CEL's employee levels, in the order their own dropdown shows them. */
export const LEVELS = [
  'office-staff',
  'apprentice-1',
  'apprentice-2',
  'apprentice-3',
  'apprentice-4',
  'journeyperson',
] as const;

/**
 * eSub licence tier. The paper form's two mutually exclusive tick-boxes, and only
 * those: every hire gets one of the two, so there is no "not required" choice.
 */
export const ESUB_ACCESS = ['employee-esub', 'user-esub'] as const;

/** Vehicle use, in the order CEL's own dropdown shows it. */
export const VEHICLE_USE = ['na', 'incidental', 'casual', 'frequent'] as const;

/**
 * Every field that exists only on 22(b). Optional in the object, required by the
 * refinement below whenever the hire needs IT.
 */
const IT_ONLY_FIELDS = [
  'companyVisaCard',
  'companyFuelCard',
  'cellPhoneAllowance',
  'vehicleAllowance',
  'softwareOffice365',
  'softwareDropbox',
  'softwareAdobe',
  'softwareBluebeam',
  'softwareAzureQb',
] as const;

export const celNewHireSchema = z
  .object({
    hireType: z.enum(HIRE_TYPES, { error: 'Choose which kind of hire this is' }),

    // ---- General information --------------------------------------------------
    // givenName, surname — from the Entra catalogue.
    ...names.shape,

    // The paper form's Email, stored as the Entra sign-in name. Optional for a general
    // field employee, who becomes a contact. Required for a hire with IT (the account
    // needs it) and when a welcome email is requested — see the refinement below.
    userPrincipalName: z
      .union([
        ENTRA_USER_ATTRIBUTES.userPrincipalName.schema,
        z.literal('').transform(() => undefined),
      ])
      .optional(),

    startDate: z.iso.date({ error: 'Use the date picker or format (YYYY-MM-DD)' }),

    branch: z.enum(BRANCHES, { error: 'Choose a branch' }),

    jobTitle: z.string().trim().min(1, 'Required').max(120, 'Limit 120 characters'),

    level: z.enum(LEVELS, { error: 'Choose a level' }),

    // mobilePhone — from the Entra catalogue; typed in any format, stored as E.164.
    ...phone.shape,

    esubAccess: z.enum(ESUB_ACCESS, { error: 'Choose an eSub level' }),

    rehire: z.boolean(),

    vehicleUse: z.enum(VEHICLE_USE, { error: 'Choose a vehicle use level' }),

    // Filled silently: every CEL branch is in Canada, so nobody is asked. No field hint
    // names this key; the registry allows that only because it carries a default.
    usageLocation: z.literal('CA').default('CA'),

    // ---- Safety setup -----------------------------------------------------------
    whmis: z.boolean(),

    csts: z.boolean(),

    otherSafetyTraining: optionalText(200),

    siteClientSpecific: optionalText(2000),

    // ---- Other information ------------------------------------------------------
    sendWelcomeEmail: z.boolean(),

    waiveBenefitsWait: z.boolean(),

    waivePensionWait: z.boolean(),

    // 22(b) only.
    companyVisaCard: z.boolean().optional(),

    companyFuelCard: z.boolean().optional(),

    cellPhoneAllowance: z.boolean().optional(),

    vehicleAllowance: z.boolean().optional(),

    otherNotes: optionalText(4000),

    // ---- IT requirements, 22(b) only ---------------------------------------------
    hardwareRequired: optionalText(300),

    softwareOffice365: z.boolean().optional(),

    softwareDropbox: z.boolean().optional(),

    softwareAdobe: z.boolean().optional(),

    softwareBluebeam: z.boolean().optional(),

    softwareAzureQb: z.boolean().optional(),

    otherSoftware: optionalText(300),
  })
  .superRefine((value, ctx) => {
    // Both reasons the email box can be mandatory, reported on that box so the message
    // lands where the user has to act. The account first: it is the sign-in name.
    if (value.userPrincipalName === undefined) {
      if (getsEntraAccount(value.hireType)) {
        ctx.addIssue({
          code: 'custom',
          path: ['userPrincipalName'],
          message: 'A hire with IT gets a CEL account, which needs an email address',
        });
      } else if (value.sendWelcomeEmail) {
        ctx.addIssue({
          code: 'custom',
          path: ['userPrincipalName'],
          message: 'An email address is required to send a welcome email',
        });
      }
    }

    if (value.hireType !== 'employee-with-it') return;

    // Every tick-box the IT section asks for has to be answered once it is on screen.
    // `hardwareRequired` and `otherSoftware` stay optional — both are blank on CEL's own
    // filled example.
    for (const field of IT_ONLY_FIELDS) {
      if (value[field] === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: 'Required for a hire with IT',
        });
      }
    }
  });

export type CelNewHireInput = z.input<typeof celNewHireSchema>;
export type CelNewHire = z.output<typeof celNewHireSchema>;

/** Shown only when the hire needs IT. */
const whenIt = { field: 'hireType', equals: 'employee-with-it' } as const;

export const celNewHire = defineForm({
  id: 'cel-1048-new-hire',
  version: '2.0.0',
  // CEL, and Castle who share the form. Dynamics customer numbers.
  customers: ['1048', '0000'],
  // The first form to be automated: the submission goes to the internal app's service
  // dashboard, and the case it still raises points the technician there.
  destination: 'automation',
  title: 'CEL - onboarding',
  description:
    'A general field employee is added as a contact only. Choose "Employee with IT" if this person needs a CEL account or any IT setup.',
  schema: celNewHireSchema,
  // The case is about the hire, so the queue reads "CEL - onboarding - Ron Swanson".
  caseTitle: (payload) => `${payload.givenName} ${payload.surname}`,
  fields: [
    {
      name: 'hireType',
      label: 'Hire type',
      widget: 'select',
      options: [
        { value: 'field-employee', label: 'General field employee' },
        { value: 'employee-with-it', label: 'Employee with IT' },
      ],
    },

    // ---- General Information --------------------------------------------------
    ...names.fields,
    {
      name: 'userPrincipalName',
      label: 'Email',
      widget: 'email',
      section: SECTIONS.general,
    },
    { name: 'startDate', label: 'Start date', widget: 'date', section: SECTIONS.general },
    {
      name: 'branch',
      label: 'Branch',
      widget: 'select',
      section: SECTIONS.general,
      options: [
        { value: '100-saskatchewan', label: '100-Saskatchewan' },
        { value: '200-manitoba', label: '200-Manitoba' },
        { value: '300-calgary', label: '300-Calgary' },
        { value: '320-camrose', label: '320-Camrose' },
        { value: '360-red-deer', label: '360-Red Deer' },
        { value: '600-corporate', label: '600-Corporate' },
      ],
    },
    {
      name: 'jobTitle',
      label: 'Job title',
      widget: 'text',
      placeholder: 'e.g. Electrician',
      section: SECTIONS.general,
    },
    {
      name: 'level',
      label: 'Level',
      widget: 'select',
      section: SECTIONS.general,
      options: [
        { value: 'office-staff', label: 'Office Staff' },
        { value: 'apprentice-1', label: '1st Year Apprentice' },
        { value: 'apprentice-2', label: '2nd Year Apprentice' },
        { value: 'apprentice-3', label: '3rd Year Apprentice' },
        { value: 'apprentice-4', label: '4th Year Apprentice' },
        { value: 'journeyperson', label: 'Journeyperson' },
      ],
    },
    ...phone.fields,
    {
      name: 'esubAccess',
      label: 'eSub access',
      widget: 'select',
      section: SECTIONS.general,
      options: [
        { value: 'employee-esub', label: 'Employee' },
        { value: 'user-esub', label: 'User' },
      ],
    },
    { name: 'rehire', label: 'Rehire', widget: 'checkbox', section: SECTIONS.general },
    {
      name: 'vehicleUse',
      label: 'Vehicle use requested',
      widget: 'select',
      section: SECTIONS.general,
      helpText:
        'Vehicle use is subject to Drivers Abstract review and approval by CEL Management and the insurance carrier.',
      options: [
        { value: 'na', label: 'N/A - Will not be driving at all' },
        { value: 'incidental', label: 'Incidental - 1 off material/coffee runs' },
        { value: 'casual', label: 'Casual - 2-3 times/week' },
        { value: 'frequent', label: 'Frequent - Daily use, but not assigned to a vehicle' },
      ],
    },

    // ---- Safety Setup -----------------------------------------------------------
    { name: 'whmis', label: 'WHMIS required', widget: 'checkbox', section: SECTIONS.safety },
    { name: 'csts', label: 'CSTS required', widget: 'checkbox', section: SECTIONS.safety },
    {
      name: 'otherSafetyTraining',
      label: 'Other safety training',
      widget: 'text',
      helpText: 'Optional. Anything beyond WHMIS and CSTS.',
      section: SECTIONS.safety,
    },
    {
      name: 'siteClientSpecific',
      label: 'Site / client specific requirements',
      widget: 'textarea',
      helpText: 'Optional. Orientations or tickets required for the site this hire will work on.',
      section: SECTIONS.safety,
    },

    // ---- Other Information ------------------------------------------------------
    {
      name: 'sendWelcomeEmail',
      label: 'Send welcome email',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
    },
    {
      name: 'waiveBenefitsWait',
      label: 'Waive benefits waiting period',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
    },
    {
      name: 'waivePensionWait',
      label: 'Waive pension waiting period',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
    },
    {
      name: 'companyVisaCard',
      label: 'Company VISA card',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
      visibleWhen: whenIt,
    },
    {
      name: 'companyFuelCard',
      label: 'Company fuel card',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
      visibleWhen: whenIt,
    },
    {
      name: 'cellPhoneAllowance',
      label: 'Cell phone allowance',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
      visibleWhen: whenIt,
    },
    {
      name: 'vehicleAllowance',
      label: 'Vehicle allowance',
      widget: 'checkbox',
      section: SECTIONS.otherInformation,
      visibleWhen: whenIt,
    },
    {
      name: 'otherNotes',
      label: 'Other notes',
      widget: 'textarea',
      helpText: 'Optional. Anything else HR or the branch needs to know.',
      section: SECTIONS.otherInformation,
    },

    // ---- IT Requirements ----------------------------------------------------------
    {
      name: 'hardwareRequired',
      label: 'Hardware required',
      widget: 'text',
      helpText: 'Optional. All monitors, keyboards etc. are to be sourced locally.',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
    {
      name: 'softwareOffice365',
      label: 'Microsoft Office 365',
      widget: 'checkbox',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
    {
      name: 'softwareDropbox',
      label: 'DropBox',
      widget: 'checkbox',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
    {
      name: 'softwareAdobe',
      label: 'Adobe',
      widget: 'checkbox',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
    {
      name: 'softwareBluebeam',
      label: 'BlueBeam',
      widget: 'checkbox',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
    {
      name: 'softwareAzureQb',
      label: 'Azure/QB',
      widget: 'checkbox',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
    {
      name: 'otherSoftware',
      label: 'Other software',
      widget: 'text',
      helpText: 'Optional. Anything not listed above.',
      section: SECTIONS.itRequirements,
      visibleWhen: whenIt,
    },
  ],
});
